import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { StatusBadge } from "@/lib/certificates";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/EmptyState";
import { TableSkeleton } from "@/components/Skeletons";
import { Plus, Search, Loader2, FileText, ChevronRight } from "lucide-react";

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return s;
  }
}

export default function Certificates() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [pkg, setPkg] = useState("all");

  const packageLabel = (p) => p || "Not Specified";
  const filtered = pkg === "all" ? rows : rows.filter((r) => (pkg === "none" ? !r.installation_package : r.installation_package === pkg));

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/installations", { params: q ? { q } : {} });
      setRows(data);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(query.trim() || undefined), 350);
    return () => clearTimeout(t);
  }, [query, load]);

  return (
    <DashboardLayout title="Certificates">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Installation History</p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">
            {isAdmin ? "All installation certificates" : "Your installation certificates"}
          </h2>
          <p className="mt-1 text-sm text-neutral-500">Every installation you've recorded, newest first.</p>
        </div>
        <button
          onClick={() => navigate("/certificates/new")}
          className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-[#FF1C2A]"
          data-testid="new-certificate-btn"
        >
          <Plus className="h-4 w-4" />
          New Installation Certificate
        </button>
      </div>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search customer, email, VIN, cert #, make, model…"
            className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] pl-10 text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]"
            data-testid="certificate-search"
          />
        </div>
        <Select value={pkg} onValueChange={setPkg}>
          <SelectTrigger className="h-11 w-full rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white sm:w-48" data-testid="certificate-package-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
            <SelectItem value="all">All Packages</SelectItem>
            <SelectItem value="Essential">Essential</SelectItem>
            <SelectItem value="Complete">Complete</SelectItem>
            <SelectItem value="Ultimate">Ultimate</SelectItem>
            <SelectItem value="none">Not Specified</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <TableSkeleton rows={6} cols={isAdmin ? 6 : 5} />
      ) : (
      <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-[#141414]" data-testid="certificates-table">
        {filtered.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={query || pkg !== "all" ? "No matching certificates" : "No certificates yet"}
            description={query || pkg !== "all" ? "Try a different search term or clear your filters." : (isAdmin ? "Certificates issued by installers will appear here." : "Create your first installation certificate to get started.")}
            actionLabel={!isAdmin && !query ? "Create Certificate" : undefined}
            onAction={!isAdmin && !query ? () => navigate("/certificates/new") : undefined}
            testid="certificates-empty"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/[0.06] text-xs uppercase tracking-wider text-neutral-500">
                  <th className="px-6 py-4 font-medium">Certificate #</th>
                  <th className="px-6 py-4 font-medium">Customer</th>
                  {isAdmin && <th className="px-6 py-4 font-medium">Installer</th>}
                  <th className="px-6 py-4 font-medium">Vehicle</th>
                  <th className="px-6 py-4 font-medium">Package</th>
                  <th className="px-6 py-4 font-medium">Install Date</th>
                  <th className="px-6 py-4 font-medium">Follow Up</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                  <th className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/certificates/${r.id}`)}
                    className="cursor-pointer border-b border-white/[0.04] transition-colors hover:bg-white/[0.03]"
                    data-testid={`certificate-row-${r.certificate_number}`}
                  >
                    <td className="px-6 py-4 font-mono text-sm text-white">{r.certificate_number}</td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-white">{r.customer_name}</p>
                      <p className="text-xs text-neutral-500">{r.customer_email}</p>
                    </td>
                    {isAdmin && <td className="px-6 py-4 text-sm text-neutral-300">{r.installer_business}</td>}
                    <td className="px-6 py-4 text-sm text-neutral-300">{r.vehicle_year} {r.vehicle_make} {r.vehicle_model}</td>
                    <td className="px-6 py-4 text-sm"><span className={r.installation_package ? "text-neutral-300" : "text-neutral-600"}>{packageLabel(r.installation_package)}</span></td>
                    <td className="px-6 py-4 text-sm text-neutral-400">{fmtDate(r.installation_date)}</td>
                    <td className="px-6 py-4 text-sm text-neutral-400">{fmtDate(r.follow_up_date)}</td>
                    <td className="px-6 py-4"><StatusBadge status={r.certificate_status} /></td>
                    <td className="px-6 py-4 text-right text-neutral-600"><ChevronRight className="ml-auto h-4 w-4" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
    </DashboardLayout>
  );
}
